<script>import { trackComponent } from "@sentry/svelte";
trackComponent({ "trackInit": true, "trackUpdates": true, "componentName": "Input" });
import { Label, LabelHint, LabelError } from "../../elements/index.js";
import { onMount } from "svelte";
import { t } from "svelte-i18n";
import { v4 as uuid } from "uuid";
import { handleInputError } from "../../common/validation.js";
export let value;
export let name;
export let label = void 0;
export let hint = void 0;
export let placeholder = void 0;
export let isDisabled = false;
export let isReadonly = false;
export let isRequired = true;
export let hasMinItems = false;
export let minLength = void 0;
export let maxLength = void 0;
export let pattern = void 0;
export let minimum = void 0;
export let maximum = void 0;
export let step = void 0;
export let autocomplete = "on";
export let keyboard = "text";
export let hasStyle = true;
export let validation = void 0;
let input = null;
let id = "";
let isTouched = false;
let error = null;
const setCustomValidation = (validation2) => {
    if (input === null) {
        return;
    }
    if (!isTouched) {
        return;
    }
    if (validation2 === void 0) {
        return;
    }
    if (validation2 === "") {
        input.setAttribute("data-custom-validation", "");
    }
    else {
        input.setAttribute("data-custom-validation", validation2);
    }
    error = handleInputError(input);
};
const validate = (event) => {
    const TARGET = event.target;
    if (event.type === "input" && !isTouched) {
        return;
    }
    error = handleInputError(TARGET);
};
const trimValue = () => {
    value = typeof value === "string" ? value.trim() : value;
};
const setTouched = (event) => {
    trimValue();
    isTouched = true;
    validate(event);
};
$: value = value === "" ? void 0 : value;
$: setCustomValidation(validation);
onMount(() => {
    id = uuid();
});
export const focus = () => {
    input?.focus();
};
</script>

<!-- eslint-disable max-lines -->

<div class = 's8-form-row s8-input' class:s8-no-label = { label === null }>

    {#if label !== null || (hint !== null && hint !== undefined)}
        <div class = 's8-header'>
            <Label { id } { label } { isDisabled } isRequired = { isRequired || hasMinItems } />
            <LabelHint { id } { hint } { isDisabled } />
        </div>
    {/if}

    <div class = 's8-input-box-outer'>

        {#if 'beforeInput' in $$slots }
            <slot name = 'beforeInput' />
        {/if}

        <span class = 's8-input-box-inner'>

            <input
                bind:value
                bind:this = { input }
                on:change = { validate }
                on:input = { validate }
                on:invalid = { setTouched }
                on:blur = { setTouched }
                on:blur
                on:click
                on:keydown
                { id }
                { pattern }
                { autocomplete }
                { step }
                { name }
                inputmode = { keyboard }
                placeholder = { $t(placeholder ?? '') }
                max = { maximum }
                min = { minimum }
                maxlength = { maxLength }
                minlength = { minLength }
                required = { isRequired }
                readonly = { isReadonly }
                disabled = { isDisabled }
                class:s8-touched = { isTouched }
                class:s8-no-style = { !hasStyle }
                data-custom-validation = { validation }
                { ...$$restProps }
            />

        </span>

        {#if 'afterInput' in $$slots }
            <slot name = 'afterInput' />
        {/if}

    </div>

    {#if !isDisabled}
        <div class = 's8-footer'>
            <LabelError { id } { error } />
        </div>
    {/if}

</div>

<style>

    div.s8-input {
        position: relative;
        padding-bottom: 2px;
        margin-bottom: 8px;
        width: 100%;
        display: flex;
        flex-direction: column;
        row-gap: 2px;
    }

        div.s8-input.s8-no-label {
            padding-top: 2px;
        }

        div.s8-input div.s8-header {
            display: flex;
            justify-content: space-between;
            min-height: 10px;
        }

        div.s8-input div.s8-footer {
            min-height: 10px;
        }

        div.s8-input div.s8-input-box-outer {
            display: flex;
            position: relative;
        }

        div.s8-input div.s8-input-box-outer span.s8-input-box-inner {
                position: relative;
                width: 100%;
            }

        div.s8-input div.s8-input-box-outer span.s8-input-box-inner input {
                    box-sizing: border-box;
                    height: 36px;
                    width: 100%;
                    padding: 0px 10px;
                    border: 0px;

                    outline: none;
                    border-radius: 6px;

                    font-size: inherit;
                    cursor: text;

                    transition:
                        background-color 0.3s ease-in-out,
                        box-shadow 0.3s ease-in-out,
                        color 0.3s ease-in-out;
                }

        div.s8-input div.s8-input-box-outer span.s8-input-box-inner input::-moz-placeholder {
                        color: rgba(39,39,17,0.36);
                        -moz-transition: color 0.3s ease-in-out;
                        transition: color 0.3s ease-in-out;
                        text-overflow: ellipsis;
                    }

        div.s8-input div.s8-input-box-outer span.s8-input-box-inner input::placeholder {
                        color: rgba(39,39,17,0.36);
                        transition: color 0.3s ease-in-out;
                        text-overflow: ellipsis;
                    }

        div.s8-input div.s8-input-box-outer span.s8-input-box-inner input:not(.s8-no-style) {
                        background: #ebebea;
                        box-shadow: inset 0 1px 1px 0 rgba(39,39,17,0.12);
                    }

        div.s8-input div.s8-input-box-outer span.s8-input-box-inner input:not(.s8-no-style):hover:not(:disabled):not(:focus),
                        div.s8-input div.s8-input-box-outer span.s8-input-box-inner input:not(.s8-no-style):hover:not(:readonly):not(:focus) {
                            background: #e6e6e5;
                        }

        div.s8-input div.s8-input-box-outer span.s8-input-box-inner input:not(.s8-no-style):focus {
                            background: #e3eae6;
                            box-shadow: inset 0 1px 1px 0 rgba(88, 159, 119, 0.24);
                        }

        div.s8-input div.s8-input-box-outer span.s8-input-box-inner input:not(.s8-no-style):focus::-moz-placeholder {
                                color: rgba(68,131,95,0.48);
                            }

        div.s8-input div.s8-input-box-outer span.s8-input-box-inner input:not(.s8-no-style):focus::placeholder {
                                color: rgba(68,131,95,0.48);
                            }

        div.s8-input div.s8-input-box-outer span.s8-input-box-inner input:not(.s8-no-style).s8-touched:invalid:not(:focus) {
                            background: #ffe6e8;
                            box-shadow: inset 0 1px 1px 0 rgba(239,35,59,0.24);
                            color: rgba(239,35,59,1);
                        }

        div.s8-input div.s8-input-box-outer span.s8-input-box-inner input:not(.s8-no-style).s8-touched:invalid:not(:focus)::-moz-placeholder {
                                color: rgba(239,35,59,0.48);
                            }

        div.s8-input div.s8-input-box-outer span.s8-input-box-inner input:not(.s8-no-style).s8-touched:invalid:not(:focus)::placeholder {
                                color: rgba(239,35,59,0.48);
                            }

        div.s8-input div.s8-input-box-outer span.s8-input-box-inner input:not(.s8-no-style):-moz-read-only {
                        background: #f7f7f6;
                        box-shadow: initial;
                        color: rgba(39,39,17,0.24);
                        cursor: default;
                    }

        div.s8-input div.s8-input-box-outer span.s8-input-box-inner input:disabled,
                    div.s8-input div.s8-input-box-outer span.s8-input-box-inner input:not(.s8-no-style):read-only {
                        background: #f7f7f6;
                        box-shadow: initial;
                        color: rgba(39,39,17,0.24);
                        cursor: default;
                    }

        div.s8-input div.s8-input-box-outer span.s8-input-box-inner input:disabled::-moz-placeholder, div.s8-input div.s8-input-box-outer span.s8-input-box-inner input:not(.s8-no-style):read-only::-moz-placeholder {
                            color: rgba(39,39,17,0.24);
                        }

        div.s8-input div.s8-input-box-outer span.s8-input-box-inner input:not(.s8-no-style):-moz-read-only::placeholder {
                            color: rgba(39,39,17,0.24);
                        }

        div.s8-input div.s8-input-box-outer span.s8-input-box-inner input:disabled::placeholder, div.s8-input div.s8-input-box-outer span.s8-input-box-inner input:not(.s8-no-style):read-only::placeholder {
                            color: rgba(39,39,17,0.24);
                        }</style>

<!-- eslint-enable -->